import isEqual from 'lodash.isequal'

import { DeviceAssignment } from '@/apps/brelag/common/models/equipment'
import { Link, SenderChannel } from '@/apps/brelag/common/models/link'

import { ProjectEditor } from '../projectEditor'
import { DOMINO_SWISS_MODULE_IDS } from './codeBlockEditor/codeBlockFunctions'
import {
  KnockautObject,
  SymconModuleType,
  SymconObjectType,
} from './interfaces'
import { KnockautHandler } from './knockautHandler'

export class KnockautDeviceHandler {
  private knockautHandler: KnockautHandler
  private projectEditor: ProjectEditor

  constructor(knockautHandler: KnockautHandler) {
    this.knockautHandler = knockautHandler
    this.projectEditor = knockautHandler.projectEditor
  }

  get fusionOneID() {
    return this.knockautHandler.fusionOne.id
  }

  get deviceHandler() {
    return this.projectEditor.deviceHandler
  }

  get equipmentHandler() {
    return this.projectEditor.equipmentHandler
  }

  /**
   * Given a list of receivers, find all sender channels that are linked to this exact set of receivers.
   * Receivers that are linked as "repeating only" have to be ignored.
   * @param receiverIds
   */
  findLinkedSenderChannels(receiverIds: string[]): SenderChannel[] {
    const receivers = new Set(receiverIds)

    // Find a sender/channel combo where exactly these receivers have a link (no more, no less)
    const matches = []
    for (const sender of this.projectEditor.senders) {
      // Get all links and separate into channels. Ignore all links that are "repeating"
      const links = this.deviceHandler.findLinks(sender.id)
      const channelMap = new Map<number, Set<string>>()
      for (const link of links) {
        // TODO: What if link is repeatingOnly but link.receiver is part of the current selection
        // Ignore repeating receivers here
        if (Link.isRepeatingOnly(link)) {
          continue
        }
        // Ignore links of FusionONE/eGate (=the receiver in the link)
        if (Link.hasEgateConfig(link)) {
          continue
        }
        if (!channelMap.has(link.sender_channel)) {
          channelMap.set(link.sender_channel, new Set())
        }
        channelMap.get(link.sender_channel).add(link.receiver)
      }

      for (const [channel, value] of channelMap.entries()) {
        if (isEqual(value, receivers)) {
          // Found exact match
          matches.push({
            sender: sender,
            channel,
          })
        }
      }
    }
    return matches
  }

  /**
   * Searches all channels of all senders for selection of
   * @param receiverIds
   */
  findSenderAndChannel(receiverIds: Set<string>): {
    sender: DeviceAssignment
    channel: number
  }[] {
    const results = []
    this.deviceHandler.senderList.forEach((sender) => {
      const equipment = this.equipmentHandler.getEquipmentFromDevice(sender)
      for (let i = 0; i < equipment.channel_count; i++) {
        const links = this.deviceHandler.findLinks(sender.id, null, i)
        const linkedReceivers = new Set(links.map((link) => link.receiver))
        if (linkedReceivers === receiverIds) {
          results.push({ sender, i })
        }
      }
    })
    return results
  }

  async createSensorGroup(
    selectedSenders: {
      channel_count: number
      icon: string
      selectedChannel: number
      device: DeviceAssignment
    }[]
  ) {
    // Make sure FusionONE is selected
    await this.projectEditor.selectReceiver(this.knockautHandler.fusionOne.id)

    // Link FusionONE with sender channels
    for (const sender of selectedSenders) {
      await this.projectEditor.selectSenderAndChannel(
        sender.device.id,
        sender.selectedChannel
      )
      await this.projectEditor.updateTeachingOptions()
      await this.projectEditor.createLinks(
        [...this.projectEditor.unlinkedTeachingOptions],
        [sender.device.id],
        sender.selectedChannel
      )
    }

    const deviceGroupsBefore: number[] = this.knockautHandler.deviceGroups.map(
      (devGroup) => devGroup.id
    )

    // Sync new config
    await this.knockautHandler.syncEgateConfigToSymcon()
    await this.projectEditor.updateLinkedState()

    // Find the newly created sensor groups and set start logging all of their variables
    const deviceGroupsAfter: number[] = this.knockautHandler.deviceGroups.map(
      (devGroup) => devGroup.id
    )
    for (const id of deviceGroupsAfter) {
      if (!deviceGroupsBefore.includes(id)) {
        this.knockautHandler.archiveHandler.setLoggingStatusInstance(id, true)
      }
    }
  }

  /**
   * Creates a new device group. Creates links for currently selected receivers and FusionONE and uploads new eGate config
   * @param senderID
   * @param channel
   */
  async createDeviceGroup(
    senderChannels: SenderChannel[],
    selectedReceivers: string[]
  ) {
    for (const senderChannel of senderChannels) {
      const senderID = senderChannel.sender
      const channel = senderChannel.channel

      // If there are already linked receivers , unlink those (unless they are repating only)
      const links = this.deviceHandler.findLinks(senderID, undefined, channel)
      // Exclude any links that already exist for the current receiver selection
      const deleteLinks = links.filter((link) => {
        // Exclude existing links of selected receivers
        if (selectedReceivers.indexOf(link.receiver) > -1) {
          if (Link.isRepeatingOnly(link)) {
            // Since repeating-only links are not controlled by FusionOne, we need to delete the existing link and create a new one
            return true
          } else {
            return false
          }
        } else if (link.receiver === this.knockautHandler.fusionOne.id) {
          return false
        } else {
          // Only delete non-repeating-only links
          return !Link.isRepeatingOnly(link)
        }
      })
      // Delete any remaining links on the sender-channel
      await this.deviceHandler.deleteLinks(deleteLinks)

      const teachingOptions = this.projectEditor.getTeachingOptions(
        [...selectedReceivers, this.knockautHandler.fusionOne.id],
        senderID,
        channel
      )

      await this.projectEditor.createLinks(teachingOptions, [senderID], channel)
    }

    // Sync new config
    await this.knockautHandler.syncEgateConfigToSymcon()
    await this.projectEditor.updateLinkedState()
  }

  /**
   * Deletes a device group. Deletes all links.
   * @param deviceGroup
   */
  async deleteDeviceGroup(deviceGroup: KnockautObject) {
    // The only connection that we can make from the Symcon Object back to out devices is throught he StorageID
    const storageID = deviceGroup.data.configuration.ID
    // Find link to sender/channel which corresponds to this StorageID
    const links = this.projectEditor.deviceHandler.findLinks(
      undefined,
      this.fusionOneID
    )
    let linkedSender: string = null
    let linkedChannel: number = null
    for (const link of links) {
      if (link.options.keyStorageId === storageID) {
        linkedSender = link.sender
        linkedChannel = link.sender_channel
      }
    }
    // If we have found this link, the FusionONE is still connected to the sender channel.
    // Otherwise the link was deleted externally (through the Planer)
    if (linkedSender && linkedChannel !== null) {
      // Only delete the link of the FusionONE to the sender, not of any receivers
      const deleteLinks = this.projectEditor.deviceHandler.findLinks(
        linkedSender,
        this.fusionOneID,
        linkedChannel
      )
      await this.projectEditor.deviceHandler.deleteLinks(deleteLinks)
    }
    await this.projectEditor.updateLinkedState()
  }

  /**
   * Returns true if obj is a instance of given modules, false otherwise
   */
  isInstanceOfModule(moduleIds: string[], obj: KnockautObject): boolean {
    return (
      obj.type === SymconObjectType.INSTANCE &&
      obj.data.moduleType === SymconModuleType.DEVICE &&
      moduleIds.includes(obj.data.moduleID)
    )
  }

  /**
   * Returns true if obj is a Dominoswiss Instance, false otherwise
   */
  isDominoSwissInstance(obj: KnockautObject): boolean {
    return this.isInstanceOfModule(DOMINO_SWISS_MODULE_IDS, obj)
  }
}
